import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Descriptions, Input, message, Modal, Space, Steps, Table, Tag, Typography } from 'antd';
import { DownloadOutlined, FileExcelOutlined, LoadingOutlined } from '@ant-design/icons';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GET_BILL_DETAIL } from '../../../graphql/queries/settlement/getSettlementBillQueries';
import { getUserInfo } from '../../../utils/auth';
import { useTranslation } from 'react-i18next';
import { getNodeStatusMeta, getTaskStatusMeta } from './utils/workflowStatus';
import { isWorkflowStaleStateError, resolveWorkflowErrorMessage } from './utils/workflowErrorMessage';
import { resolveWorkflowMessage } from '../../../utils/graphqlUtils';
import { formatInstantInIanaZone } from '../../../utils/timezoneFormatter';
import { formatSettlementAmountFromCents } from '../../../utils/settlementAmountFormatter';
import { WORKFLOW_BIZ_TYPE_QC_PENALTY_UPLOAD } from './constants/workflowBizTypes';

const WORKFLOW_INSTANCE_DETAIL = gql`
  query WorkflowInstanceDetail($instanceId: Long!) {
    workflowInstanceDetail(instanceId: $instanceId) {
      success
      message
      messageKey
      data {
        instance { id bizType bizId instanceStatus initiatorId createdAt contextJson }
        nodes { id nodeKey nodeName sortOrder nodeStatus approverSelectorJson actedByUserId actedAt remark }
        actionLogs { id action operatorName operatorUserId remark createdAt }
      }
    }
  }
`;

const GET_QC_PENALTY_SOURCE_URL = gql`
  query GetQcPenaltyUploadSourceUrl($taskId: Long!) {
    getQcPenaltyUploadSourceUrl(taskId: $taskId)
  }
`;

const GET_WORKFLOW_ADJUSTMENT_ATTACHMENT_URL = gql`
  query GetWorkflowAdjustmentAttachmentUrl($instanceId: Long!, $objectKey: String!) {
    getWorkflowAdjustmentAttachmentUrl(instanceId: $instanceId, objectKey: $objectKey) {
      success
      message
      messageKey
      data
    }
  }
`;

/** 审批实例 contextJson 里携带的调账附件元数据 */
interface WorkflowAdjustmentAttachment {
  objectKey: string;
  fileName: string;
  fileSize?: number;
  contentType?: string | null;
}

const APPROVE_NODE = gql`
  mutation ApproveWorkflowNode($instanceId: Long!, $nodeKey: String!, $remark: String) {
    approveWorkflowNode(instanceId: $instanceId, nodeKey: $nodeKey, remark: $remark) { id instanceStatus success message messageKey }
  }
`;

const REJECT_NODE = gql`
  mutation RejectWorkflowNode($instanceId: Long!, $nodeKey: String!, $remark: String!) {
    rejectWorkflowNode(instanceId: $instanceId, nodeKey: $nodeKey, remark: $remark) { id instanceStatus success message messageKey }
  }
`;

const WorkflowAdjustmentReviewDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const routePrefix = location.pathname.startsWith('/qualitycontrol') ? '/qualitycontrol/settlement' : '/settlement';
  const { instanceId, bizId } = useParams<{ instanceId: string; bizId: string }>();
  const [remark, setRemark] = useState('');
  const [rejectVisible, setRejectVisible] = useState(false);
  const [approveVisible, setApproveVisible] = useState(false);
  const userId = Number(getUserInfo()?.account?.id || 0);
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data, loading, refetch } = useQuery(WORKFLOW_INSTANCE_DETAIL, {
    variables: { instanceId },
    fetchPolicy: 'network-only',
  });
  const workflowDetailResult = data?.workflowInstanceDetail;
  const workflowDetail = workflowDetailResult?.data;
  const workflowBizType = workflowDetail?.instance?.bizType;
  const isQcPenaltyReview = workflowBizType === WORKFLOW_BIZ_TYPE_QC_PENALTY_UPLOAD;
  // 质控罚单审批的 bizId 是罚单任务 id，不是账单 id，不能去查账单详情（否则报"账单不存在"）。
  // bizType 要等审批实例详情返回后才知道，所以必须等 workflowBizType 就绪、且确认不是质控罚单后才查账单，
  // 否则首屏 workflowBizType 还是 undefined，会拿任务 id 当账单 id 发出一次必然失败的请求。
  const { data: billData } = useQuery(GET_BILL_DETAIL, {
    variables: { billId: bizId },
    skip: !bizId || !workflowBizType || isQcPenaltyReview,
  });
  const [fetchSourceUrl, { loading: sourceUrlLoading }] = useLazyQuery(GET_QC_PENALTY_SOURCE_URL, {
    fetchPolicy: 'network-only',
  });
  const [fetchAdjustmentAttachmentUrl] = useLazyQuery(GET_WORKFLOW_ADJUSTMENT_ATTACHMENT_URL, {
    fetchPolicy: 'network-only',
  });
  const [approveNode, { loading: approving }] = useMutation(APPROVE_NODE);
  const [rejectNode, { loading: rejecting }] = useMutation(REJECT_NODE);

  const detail = workflowDetail;
  const bill = billData?.getBillDetail?.bill;
  const nodes = detail?.nodes ?? [];
  const currentNode = [...nodes]
    .filter((n: any) => n.nodeStatus === 1)
    .sort((a: any, b: any) => a.sortOrder - b.sortOrder)[0];
  const activeInstance = detail?.instance?.instanceStatus === 1 || detail?.instance?.instanceStatus === 2;
  const canOperate = useMemo(() => {
    if (!currentNode || !activeInstance) return false;
    try {
      const approvers = JSON.parse(currentNode.approverSelectorJson || '[]') as number[];
      return approvers.includes(userId);
    } catch {
      return false;
    }
  }, [activeInstance, currentNode, userId]);

  const adjustmentItems = useMemo(() => {
    try {
      return JSON.parse(detail?.instance?.contextJson || '{}')?.adjustmentItems ?? [];
    } catch {
      return [];
    }
  }, [detail?.instance?.contextJson]);

  const qcPenaltyContext = useMemo(() => {
    try {
      return JSON.parse(detail?.instance?.contextJson || '{}');
    } catch {
      return {};
    }
  }, [detail?.instance?.contextJson]);

  const handleDownloadQcPenaltySource = async () => {
    if (!bizId) return;
    try {
      const result = await fetchSourceUrl({ variables: { taskId: bizId } });
      const url = result.data?.getQcPenaltyUploadSourceUrl;
      if (!url) {
        message.warning('源文件暂不可下载');
        return;
      }
      window.open(url, '_blank');
    } catch {
      message.error('获取下载链接失败');
    }
  };

  const handleDownloadAdjustmentAttachment = async (objectKey: string) => {
    if (!instanceId) return;
    try {
      const result = await fetchAdjustmentAttachmentUrl({
        variables: { instanceId, objectKey },
      });
      const urlResult = result.data?.getWorkflowAdjustmentAttachmentUrl;
      if (!urlResult?.success) {
        message.error(resolveWorkflowMessage(urlResult, '获取下载链接失败'));
        return;
      }
      const url = urlResult.data
      if (!url) {
        message.warning('附件暂不可下载');
        return;
      }
      window.open(url, '_blank');
    } catch {
      message.error('获取下载链接失败');
    }
  };

  const formatLocalTime = (time?: string | null) => {
    if (!time) return '-';
    const date = new Date(time);
    return Number.isNaN(date.getTime()) ? '-' : formatInstantInIanaZone(date, browserTimeZone);
  };

  const initiateLog = (detail?.actionLogs ?? []).find((log: any) => log.action === 'CREATE');

  useEffect(() => {
    if (workflowDetailResult && workflowDetailResult.success === false) {
      message.error(resolveWorkflowMessage(workflowDetailResult, '加载审批详情失败'));
    }
  }, [workflowDetailResult]);

  const onApprove = async () => {
    if (!currentNode) return;
    if (remark.length > 500) return message.error('备注不能超过500字符');
    try {
      const result = await approveNode({ variables: { instanceId, nodeKey: currentNode.nodeKey, remark: remark || null } });
      if (result.data?.approveWorkflowNode?.success === false) {
        message.error(resolveWorkflowMessage(result.data.approveWorkflowNode, '审核通过失败，请刷新页面后重试'));
        return;
      }
      message.success('审核通过成功');
      setApproveVisible(false);
      setRemark('');
      refetch();
    } catch (error) {
      const friendlyMessage = resolveWorkflowErrorMessage(error, '审核通过失败，请刷新页面后重试');
      if (isWorkflowStaleStateError(error)) {
        message.warning(friendlyMessage);
        setApproveVisible(false);
        setRemark('');
        refetch();
        return;
      }
      message.error(friendlyMessage);
    }
  };

  const onReject = async () => {
    if (!currentNode) return;
    if (!remark.trim()) return message.error('驳回必须填写备注');
    if (remark.length > 500) return message.error('备注不能超过500字符');
    try {
      const result = await rejectNode({ variables: { instanceId, nodeKey: currentNode.nodeKey, remark } });
      if (result.data?.rejectWorkflowNode?.success === false) {
        message.error(resolveWorkflowMessage(result.data.rejectWorkflowNode, '驳回失败，请刷新页面后重试'));
        return;
      }
      message.success('驳回成功');
      setRejectVisible(false);
      setRemark('');
      refetch();
    } catch (error) {
      const friendlyMessage = resolveWorkflowErrorMessage(error, '驳回失败，请刷新页面后重试');
      if (isWorkflowStaleStateError(error)) {
        message.warning(friendlyMessage);
        setRejectVisible(false);
        setRemark('');
        refetch();
        return;
      }
      message.error(friendlyMessage);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <Button onClick={() => navigate(`${routePrefix}/adjustment-review`)}>返回列表</Button>
      <Card title="任务基础信息" loading={loading}>
        <Descriptions bordered column={2}>
          {isQcPenaltyReview ? (
            <>
              <Descriptions.Item label="任务ID">{bizId}</Descriptions.Item>
              <Descriptions.Item label="账单周期">
                {qcPenaltyContext.periodStart && qcPenaltyContext.periodEnd
                  ? `${formatLocalTime(qcPenaltyContext.periodStart)} ~ ${formatLocalTime(qcPenaltyContext.periodEnd)}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="提交人">{qcPenaltyContext.submitterName ?? initiateLog?.operatorName ?? '-'}</Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="客户ID">{bill?.clientId ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="客户名称">{bill?.clientName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="账单ID">{bizId}</Descriptions.Item>
              <Descriptions.Item label={`账单周期 (${bill?.timeZone || '-'})`}>
                {bill?.periodStart && bill?.periodEnd
                  ? `${formatInstantInIanaZone(new Date(bill.periodStart), bill.timeZone)} ~ ${formatInstantInIanaZone(new Date(bill.periodEnd), bill.timeZone)}`
                  : '-'}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="发起人">{initiateLog?.operatorName || detail?.instance?.initiatorId}</Descriptions.Item>
          <Descriptions.Item label="发起时间">{formatLocalTime(detail?.instance?.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="审批流程状态">
            {typeof detail?.instance?.instanceStatus === 'number' ? (() => {
              const meta = getTaskStatusMeta(detail.instance.instanceStatus, t);
              return <Tag color={meta.color}>{meta.label}</Tag>;
            })() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {isQcPenaltyReview ? (
        <Card title="罚单详情">
          <div className="mb-4 rounded border border-gray-100 bg-gray-50 px-3 py-2">
            {qcPenaltyContext.sourceFileName ? (
              <Typography.Link
                onClick={() => void handleDownloadQcPenaltySource()}
                disabled={sourceUrlLoading}
                className="inline-flex max-w-full items-center gap-2 text-[13px] leading-5"
              >
                {sourceUrlLoading ? (
                  <LoadingOutlined className="shrink-0 text-gray-400" />
                ) : (
                  <FileExcelOutlined className="shrink-0 text-green-600" />
                )}
                <span className="truncate text-gray-700">{qcPenaltyContext.sourceFileName}</span>
              </Typography.Link>
            ) : (
              <span className="text-[13px] text-gray-400">暂无上传文件</span>
            )}
          </div>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="总笔数">{qcPenaltyContext.validCount ?? qcPenaltyContext.rowCount ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="校验失败笔数">{qcPenaltyContext.failCount ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="总金额">
              {typeof qcPenaltyContext.totalAmountCents === 'number'
                ? formatSettlementAmountFromCents(String(qcPenaltyContext.totalAmountCents), 'USD')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="文件行数">{qcPenaltyContext.rowCount ?? '-'}</Descriptions.Item>
          </Descriptions>
        </Card>
      ) : (
        <Card title="调账详情">
          <Table
            rowKey={(row: any, idx) => `${row.feeItemName || row.feeItemCode}-${idx}`}
            dataSource={adjustmentItems}
            pagination={false}
            columns={[
              { title: '费用项名称', dataIndex: 'feeItemName', render: (v: string, row: any) => v || row.feeItemCode || '-' },
              { title: '调账前金额', dataIndex: 'beforeAmount', render: (v: string) => formatSettlementAmountFromCents(v, bill?.currency) },
              { title: '调账金额', dataIndex: 'adjustmentAmount', render: (v: string) => formatSettlementAmountFromCents(v, bill?.currency) },
              { title: '调账后金额', dataIndex: 'afterAmount', render: (v: string) => formatSettlementAmountFromCents(v, bill?.currency) },
              { title: '调账原因', dataIndex: 'reason', ellipsis: true, render: (v: string) => <span title={v}>{v || '-'}</span> },
              {
                title: '附件',
                dataIndex: 'attachment',
                render: (attachment: WorkflowAdjustmentAttachment | null | undefined) => {
                  if (!attachment) return '-';
                  return (
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      style={{ padding: 0 }}
                      title={attachment.fileName}
                      onClick={() => handleDownloadAdjustmentAttachment(attachment.objectKey)}
                    >
                      {attachment.fileName}
                    </Button>
                  );
                },
              },
            ]}
          />
        </Card>
      )}

      <Card title="备注信息">
        <Table
          rowKey="id"
          dataSource={(detail?.actionLogs ?? []).filter((l: any) => !!l.remark)}
          pagination={false}
          columns={[
            { title: '操作', dataIndex: 'action' },
            { title: '操作人', dataIndex: 'operatorName' },
            { title: '备注', dataIndex: 'remark' },
            { title: '时间', dataIndex: 'createdAt', render: (v: string) => formatLocalTime(v) },
          ]}
        />
      </Card>

      <Card title="审核流信息">
        <Table
          rowKey="id"
          dataSource={nodes}
          pagination={false}
          columns={[
            { title: '节点', dataIndex: 'nodeName' },
            { title: '节点Key', dataIndex: 'nodeKey' },
            {
              title: '状态',
              dataIndex: 'nodeStatus',
              render: (v: number, row: any) => {
                const meta = getNodeStatusMeta(v, t);
                return (
                  <Tag color={meta.color}>
                    {meta.label}
                  </Tag>
                );
              },
            },
            { title: '处理人', dataIndex: 'actedByUserId' },
            { title: '处理时间', dataIndex: 'actedAt', render: (v: string) => formatLocalTime(v) },
          ]}
        />
      </Card>

      <Card title="流程可视化">
        <Steps
          current={Math.max(0, nodes.findIndex((n: any) => n.id === currentNode?.id))}
          items={[...nodes].sort((a: any, b: any) => a.sortOrder - b.sortOrder).map((node: any) => ({
            title: node.nodeName,
            description: `${node.nodeKey} · ${getNodeStatusMeta(node.nodeStatus, t).label}`,
            status: node.nodeStatus === 2 ? 'finish' : node.nodeStatus === 3 ? 'error' : node.id === currentNode?.id ? 'process' : 'wait',
          }))}
        />
      </Card>

      {canOperate && (
        <Card>
          <Space>
            <Button type="primary" onClick={() => setApproveVisible(true)}>通过</Button>
            <Button danger onClick={() => setRejectVisible(true)}>驳回</Button>
          </Space>
        </Card>
      )}

      <Modal
        open={approveVisible}
        title="通过审核"
        onCancel={() => setApproveVisible(false)}
        onOk={onApprove}
        confirmLoading={approving}
      >
        <div style={{ marginBottom: 20 }}>
          <Input.TextArea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            maxLength={500}
            showCount
            placeholder="备注可选，最多500字符"
            rows={4}
          />
        </div>
      </Modal>

      <Modal
        open={rejectVisible}
        title="驳回"
        onCancel={() => setRejectVisible(false)}
        onOk={onReject}
        confirmLoading={rejecting}
      >
        <div style={{ marginBottom: 20 }}>
          <Input.TextArea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            maxLength={500}
            showCount
            placeholder="请填写驳回原因（必填，最多500字符）"
            rows={4}
          />
        </div>
      </Modal>
    </Space>
  );
};

export default WorkflowAdjustmentReviewDetailPage;
